import { useState, useEffect } from "react";
import {
  Pen,
  Trash2,
  Smile,
  Info,
  Lock,
  Plus,
  PlusCircle,
  AlertTriangle,
} from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Popover from "@radix-ui/react-popover";
import { getEmojiImage } from "../Dashboards/utils";
import AddEmotionAlert from "./AddEmotionAlert";
import { useEmotionSettings } from "@/hooks/useEmotionSettings";
import { useBootstrap } from "@/features/bootstrap";

const EmotionAlertsGrid = ({ userScope }) => {
  const { data: user } = useBootstrap();
  const [showPopup, setShowPopup] = useState(false);
  const { emotionSettingsQuery, deleteEmotionSettingMutation } =
    useEmotionSettings();

  const { data, isPending, isError } = emotionSettingsQuery;

  const {
    mutateAsync: deleteEmotionSettingMutate,
    isPending: deleteEmotionSettingMutateIsPending,
    isError: deleteEmotionSettingMutateIsError,
    error: deleteEmotionSettingMutateError,
  } = deleteEmotionSettingMutation;

  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [onMobile, setOnMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setOnMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleConfirmDelete = async () => {
    if (!ruleToDelete?._id) return;
    try {
      setDeletingId(ruleToDelete._id.toString());
      await deleteEmotionSettingMutate(ruleToDelete._id.toString());
      setConfirmOpen(false);
      setRuleToDelete(null);
    } catch (e) {
      setDeleteError(e?.message || "Failed to delete alert. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  function canModifyRule(userData, setting) {
    const roleOrder = ["123wellness", "superadmin", "admin", "teacher"];
    const scopeOrder = ["default", "district", "school", "custom"];
    const userRoleIndex = roleOrder.indexOf(userData.role);
    const settingScopeIndex = scopeOrder.indexOf(setting.scope);

    if (userRoleIndex === -1) return { canEdit: false, canDelete: false };

    if (
      setting.scope === "custom" &&
      setting.createdBy.toString() !== userData._id.toString()
    ) {
      return { canEdit: false, canDelete: false };
    }

    if (userData.role !== "123wellness") {
      if (setting.createdBy.toString() !== userData._id.toString()) {
        // ensures that shared settings (like those at the "school" scope) are protected unless the user is the creator, higher authority (e.g., "123wellness")
        // or of the same scope
        if (settingScopeIndex < userRoleIndex) {
          return { canEdit: false, canDelete: false };
        }
      }
    }

    return { canEdit: true, canDelete: true };
  }

  let allRules = [];
  if (data && data.byScope) {
    if (Array.isArray(data.byScope.default)) {
      allRules = allRules.concat(
        data.byScope.default.map((rule) => ({
          ...rule,
          scopeLabel: "Default",
        })),
      );
    }
    if (Array.isArray(data.byScope.district)) {
      allRules = allRules.concat(
        data.byScope.district.map((rule) => ({
          ...rule,
          scopeLabel: "District-wide",
        })),
      );
    }
    if (Array.isArray(data.byScope.school)) {
      allRules = allRules.concat(
        data.byScope.school.map((rule) => ({
          ...rule,
          scopeLabel: "School-wide",
        })),
      );
    }
    if (Array.isArray(data.byScope.custom)) {
      allRules = allRules.concat(
        data.byScope.custom.map((rule) => ({
          ...rule,
          scopeLabel: "Custom (Visible to me only)",
        })),
      );
    }
  }

  allRules = allRules.sort((a, b) => b.tier - a.tier);

  return (
    <div className="tw">
      <div className=" bg-white rounded flex flex-col p-3 w-full">
        {!onMobile && (
          <div className="flex flex-row gap-2 pb-2.5 items-center">
            <Smile size={14} />
            EMOTION ALERTS
            <div className="group">
              <Info size={14} className="text-[#256AA6BF]" />
              <span className="absolute z-10 hidden group-hover:block mt-1 py-2 px-3 bg-white shadow-lg rounded text-[16px] w-[30%]">
                Emotion alerts empower you to decide when you will get “alerted”
                if/when students feel specific emotions at a high rate. For
                example, you may want to know if any of your students have been
                feeling sad over 30% of the time this week. In your Student
                Supports page, students who meet the thresholds that you set
                will appear in yellow (tier 2) or red (tier 3). Administrators
                can set “schoolwide” emotion alerts. All educators can also set
                “custom” alerts (only visible to you). Bottom line, emotion
                alerts empower you, a talented and caring educator, to be
                responsive to your student's needs in powerful ways!
              </span>
            </div>
          </div>
        )}
        {isPending ? (
          <div className="flex flex-col items-center justify-center py-5 border border-[#DDDDDD] rounded-tl rounded-tr">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mb-2.5"></div>
            <div className="text-gray-500 text-[16px]">
              Loading emotion alerts...
            </div>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-5">
            <div className="text-red-600 text-[16px] mb-1.5">
              Failed to load emotion alerts. Please refresh the page.
            </div>
          </div>
        ) : (
          <>
            {deleteError && (
              <div className="mb-3 text-red-600 text-[14px] bg-red-50 border border-red-200 rounded p-2">
                {deleteError}
              </div>
            )}
            {onMobile ? (
              <div className="w-full relative">
                {allRules.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-4">
                    <PlusCircle size={24} className="text-gray-400 mb-2.5" />
                    <p className="text-gray-500 text-[16px] mb-2.5">
                      No active emotion alerts
                    </p>
                    <p className="text-gray-500 text-[16px] mb-2.5">
                      Create an alert to monitor student emotional patterns
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[50vh] overflow-y-auto pb-4 space-y-3 relative">
                    {allRules.map((rule, idx) => {
                      const threshold = `${rule.threshold_percentage}% in ${rule.days} days`;
                      const tierLabel =
                        rule.tier === 2
                          ? "T2"
                          : rule.tier === 3
                            ? "T3"
                            : rule.tier;
                      const { canEdit, canDelete } = canModifyRule(user, rule);
                      return (
                        <div
                          key={`${rule._id}-${idx}`}
                          className={`border border-[#DDDDDD] rounded p-3 bg-white`}
                        >
                          <div className="grid grid-cols-2 gap-y-3">
                            <div className="opacity-50 text-sm">EMOTION</div>
                            <div className="capitalize flex items-center gap-2 text-base">
                              <img
                                src={getEmojiImage(rule.emotion)}
                                alt={rule.emotion}
                                className="h-5 w-5"
                              />
                              {rule.emotion}
                            </div>
                            <div className="opacity-50 text-sm">THRESHOLD</div>
                            <div className="text-base">{threshold}</div>
                            <div className="opacity-50 text-sm">COUNT</div>
                            <div className="text-base">
                              {rule.threshold_count}
                            </div>
                            <div className="opacity-50 text-sm">TIER</div>
                            <div className="text-base">{tierLabel}</div>
                            <div className="opacity-50 text-sm">RULE TYPE</div>
                            <div className="text-base">{rule.scopeLabel}</div>
                          </div>
                          <div className="mt-3 flex items-center justify-center gap-3">
                            {canEdit || canDelete ? (
                              <>
                                {canEdit && (
                                  <Popover.Root>
                                    <Popover.Trigger asChild>
                                      <button
                                        className="flex items-center gap-2 px-4 py-2 rounded text-base text-[#0047CC] bg-[#D5E6FD] font-semibold"
                                        type="button"
                                      >
                                        <Pen
                                          size={12}
                                          className="text-[#0047CC]"
                                        />
                                        Edit
                                      </button>
                                    </Popover.Trigger>
                                    <Popover.Portal>
                                      <Popover.Content
                                        side="top"
                                        align="center"
                                        sideOffset={6}
                                        className="bg-black flex-wrap opacity-90 text-white text-sm max-w-[80vw] mx-8 rounded py-2 px-3 z-50 shadow"
                                      >
                                        Editing coming soon. Please feel free to
                                        edit by creating a new rule.
                                        <Popover.Arrow className="fill-black" />
                                      </Popover.Content>
                                    </Popover.Portal>
                                  </Popover.Root>
                                )}
                                {canDelete && (
                                  <button
                                    className="flex items-center gap-2 px-4 py-2 rounded text-base text-[#B50E1C] bg-[#FFDDDE] font-semibold"
                                    onClick={() => {
                                      setDeleteError("");
                                      setRuleToDelete(rule);
                                      setConfirmOpen(true);
                                    }}
                                  >
                                    <Trash2
                                      size={12}
                                      className="text-[#B50E1C]"
                                    />
                                    Delete
                                  </button>
                                )}
                              </>
                            ) : (
                              <Popover.Root>
                                <Popover.Trigger asChild>
                                  <button type="button">
                                    <Lock size={20} className="text-gray-400" />
                                  </button>
                                </Popover.Trigger>
                                <Popover.Portal>
                                  <Popover.Content
                                    side="top"
                                    align="center"
                                    sideOffset={6}
                                    className="bg-black opacity-90 text-white text-sm flex-wrap max-w-[80vw] mx-8 rounded py-2 px-3 z-50 shadow"
                                  >
                                    Please use the “Add Alert” button to create
                                    a new alert
                                    <Popover.Arrow className="fill-black" />
                                  </Popover.Content>
                                </Popover.Portal>
                              </Popover.Root>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-6 w-full bg-[#F8FAFB80] border border-[#DDDDDD] rounded-tl rounded-tr max-h-[40vh] overflow-y-scroll">
                {allRules.length > 0 && (
                  <div className="col-span-6 grid grid-cols-6 px-8 py-3 font-semibold text-[12px] uppercase border-b border-b-[#DDDDDD] sticky top-0 bg-[white]">
                    <div>Emotion</div>
                    <div>Threshold</div>
                    <div>Count</div>
                    <div>Tier</div>
                    <div>Rule Type</div>
                    <div className="text-right">Actions</div>
                  </div>
                )}
                {allRules.length === 0 ? (
                  <div className="flex flex-col col-span-6 items-center justify-center py-2.5 w-[100%]">
                    <PlusCircle size={24} className="text-gray-400 mb-2.5" />
                    <p className="text-gray-500 text-[16px] mb-2.5">
                      No active emotion alerts
                    </p>
                    <p className="text-gray-500 text-[16px] mb-2.5">
                      Create an alert to monitor student emotional patterns
                    </p>
                  </div>
                ) : (
                  allRules.map((rule, idx) => {
                    const threshold = `${rule.threshold_percentage}% in ${rule.days} days`;
                    const tierLabel =
                      rule.tier === 2
                        ? "T2"
                        : rule.tier === 3
                          ? "T3"
                          : rule.tier;

                    const { canEdit, canDelete } = canModifyRule(user, rule);

                    const openConfirm = () => {
                      setDeleteError("");
                      setRuleToDelete(rule);
                      setConfirmOpen(true);
                    };

                    return (
                      <div
                        key={`${rule._id}-${idx}`}
                        className="col-span-6 grid grid-cols-6 gap-3 items-center px-8 py-3 text-[16px] border-b border-b-[#DDDDDD]"
                      >
                        <div className="capitalize flex xl:flex-row flex-col gap-2 justify-start">
                          <img
                            src={getEmojiImage(rule.emotion)}
                            alt={rule.emotion}
                            className="h-5 w-5"
                          />
                          {rule.emotion}
                        </div>
                        <div>{threshold}</div>
                        <div>{rule.threshold_count}</div>
                        <div>{tierLabel}</div>
                        <div>{rule.scopeLabel}</div>
                        <div className="flex justify-end gap-1">
                          <Tooltip.Provider delayDuration={300}>
                            {canDelete ? (
                              <>
                                <Popover.Root>
                                  <Popover.Trigger asChild>
                                    <span>
                                      <Pen
                                        size={16}
                                        className="text-gray-600 hover:text-blue-600 cursor-pointer"
                                      />
                                    </span>
                                  </Popover.Trigger>
                                  <Popover.Portal>
                                    <Popover.Content
                                      side="top"
                                      align="end"
                                      sideOffset={6}
                                      className="bg-black opacity-90 text-white  text-[16px] rounded py-2 px-3 z-50 shadow"
                                    >
                                      Editing coming soon. Please feel free to
                                      edit by creating a new rule.
                                      <Popover.Arrow className="fill-black" />
                                    </Popover.Content>
                                  </Popover.Portal>
                                </Popover.Root>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <span
                                      className={
                                        deletingId === rule._id
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }
                                      onClick={
                                        deletingId === rule._id
                                          ? undefined
                                          : openConfirm
                                      }
                                    >
                                      <Trash2
                                        size={16}
                                        className="text-red-500 hover:text-red-700 cursor-pointer"
                                      />
                                    </span>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content
                                      side="top"
                                      align="end"
                                      sideOffset={6}
                                      className="bg-black opacity-90 text-white text-[16px] rounded py-2 px-3 z-50 shadow"
                                    >
                                      Delete this alert
                                      <Tooltip.Arrow className="fill-black" />
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </>
                            ) : (
                              <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                  <Lock
                                    size={16}
                                    className="text-gray-400 cursor-not-allowed"
                                  />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                  <Tooltip.Content
                                    side="top"
                                    align="end"
                                    sideOffset={6}
                                    className="bg-black opacity-90 text-white text-[16px] rounded py-2 px-3 z-50 shadow"
                                  >
                                    Please use the “Add Alert” button to create
                                    a new alert
                                    <Tooltip.Arrow className="fill-black" />
                                  </Tooltip.Content>
                                </Tooltip.Portal>
                              </Tooltip.Root>
                            )}
                          </Tooltip.Provider>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}

        {/* Delete confirmation modal */}
        {confirmOpen && (
          <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded sm:ml-[130px] w-[90%] sm:w-[40%] xl:w-[30%] 2xl:w-[25%] shadow">
              <div className="text-[18px] flex py-2 px-4 border-b border-b-[#DDDDDD] sticky z-10 top-0 bg-white">
                CONFIRM DELETE
              </div>
              <div className="flex flex-col items-center gap-2 bg-[#FFF5F5] border border-[#FFE6E6] p-2 rounded m-3">
                <div className="flex flex-row items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <div className="font-semibold text-[16px] text-red-700">
                    Delete Emotion Alert
                  </div>
                </div>
                <div className="text-[14px] ">
                  Are you sure you want to delete the{" "}
                  <span className="font-semibold">{`tier ${ruleToDelete?.tier}`}</span>{" "}
                  <span className="font-semibold">
                    {ruleToDelete?.scope === "default"
                      ? "default"
                      : ruleToDelete?.scope === "district"
                        ? "district-wide"
                        : ruleToDelete?.scope === "school"
                          ? "school-wide"
                          : ruleToDelete?.scope === "custom"
                            ? "custom"
                            : ""}
                  </span>{" "}
                  emotion alert when a student with at least{" "}
                  <span className="font-semibold">
                    {ruleToDelete.threshold_count}
                  </span>{" "}
                  wellness practices reports feeling{" "}
                  <span className="font-semibold">{ruleToDelete?.emotion}</span>{" "}
                  over{" "}
                  <span className="font-semibold">
                    {ruleToDelete?.threshold_percentage}%
                  </span>{" "}
                  of the time in the past{" "}
                  <span className="font-semibold">{ruleToDelete?.days}</span>{" "}
                  days? This action cannot be undone.
                </div>
              </div>
              {deleteEmotionSettingMutateIsError && (
                <div className="mb-2 text-red-600 text-[14px] bg-red-50 border border-red-200 rounded p-2">
                  {deleteEmotionSettingMutateError?.message ||
                    "Failed to delete alert."}
                </div>
              )}
              {deleteError && (
                <div className="mb-2 text-red-600 text-[14px] bg-red-50 border border-red-200 rounded p-2">
                  {deleteError}
                </div>
              )}
              <div className="flex justify-end gap-3 mb-3 mr-4">
                <button
                  className="py-2 px-3 rounded text-[16px] font-semibold transition-colors bg-gray-200 text-gray-800 hover:bg-gray-300"
                  onClick={() => {
                    if (deleteEmotionSettingMutateIsPending) return;
                    setConfirmOpen(false);
                    setRuleToDelete(null);
                  }}
                  disabled={deleteEmotionSettingMutateIsPending}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-600 text-white py-2 px-3 rounded text-[16px] font-semibold hover:bg-red-700 disabled:opacity-60"
                  onClick={handleConfirmDelete}
                  disabled={deleteEmotionSettingMutateIsPending}
                >
                  {deleteEmotionSettingMutateIsPending
                    ? "Deleting..."
                    : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showPopup && (
          <AddEmotionAlert
            show={showPopup}
            onClose={() => setShowPopup(false)}
            userScope={userScope}
          />
        )}
        <div className="w-full flex justify-center">
          <button
            onClick={() => setShowPopup(true)}
            className="flex items-center justify-center align-center gap-1 bg-[#256AA6BF] text-white py-3 w-full
            rounded-bl rounded-br transition font-bold text-[14px]"
          >
            <Plus size={15} />
            Add Alert
          </button>
        </div>
      </div>
    </div>
  );
};
export default EmotionAlertsGrid;
